import React, { useEffect, useState } from "react";
import { useUser } from "../../contexts/UserContext";
import { HiPaperClip, HiDocumentText, HiOutlineCloudUpload, HiXCircle } from "react-icons/hi";
import { FiX, FiDownload, FiClock, FiUser, FiActivity, FiCheckSquare, FiTrash2 } from "react-icons/fi";
import api from "../../utils/api";
import Loader from "../Loader";
import { format } from "date-fns";
import ProjectManagementForm from "./ProjectManagementForm";
import Select from 'react-select';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function ProjectManagementDetails({ taskId, onClose, onSuccess }) {
    const { user } = useUser();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [newStatusId, setNewStatusId] = useState('');
    const [newPriorityId, setNewPriorityId] = useState('');
    const [newAssigneeIds, setNewAssigneeIds] = useState([]);
    const [responseAttachments, setResponseAttachments] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showVersionPopup, setShowVersionPopup] = useState(false);
    const [showVersionForm, setShowVersionForm] = useState(false);
    const [versionToDelete, setVersionToDelete] = useState(null);
    const [versionForm, setVersionForm] = useState({
        version_number: '',
        notes: '',
        previous_changes_doc: null,
        updated_doc: null
    });

    const [drawerWidth, setDrawerWidth] = useState(880);
    const [isResizing, setIsResizing] = useState(false);

    const startResizing = (e) => {
        setIsResizing(true);
        e.preventDefault();
    };

    const stopResizing = () => {
        setIsResizing(false);
    };

    const resize = (e) => {
        if (isResizing) {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > 600 && newWidth < (window.innerWidth - 100)) {
                setDrawerWidth(newWidth);
            }
        }
    };

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        } else {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [isResizing]);

    const [statuses, setStatuses] = useState([]);
    const [empOptions, setEmpOptions] = useState([]);
    const priorityOptions = [
        { value: 1, label: 'Low' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'High' },
        { value: 4, label: 'Critical' }
    ];

    const handleDownload = (fileUrl, type, originalName = null) => {
        try {
            // Extract the relative path (uploads/...) from the full URL
            const relativePath = fileUrl.split(IMAGE_BASE_URL + '/')[1];

            let baseName = "";
            if (originalName) {
                baseName = originalName;
            } else {
                const parts = fileUrl.split('/');
                const fullName = parts[parts.length - 1];
                // Only split if there's a timestamp prefix (number followed by underscore)
                const match = fullName.match(/^\d+_(.+)$/);
                baseName = match ? match[1] : fullName;
            }

            const ext = baseName.includes('.') ? baseName.split('.').pop() : '';
            const nameWithoutExt = baseName.includes('.') ? baseName.substring(0, baseName.lastIndexOf('.')) : baseName;
            
            const d = new Date();
            const timestamp = `${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${d.getFullYear()}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
            
            const finalName = `${nameWithoutExt}_${type.replace(/\s+/g, '_').toLowerCase()}_${timestamp}${ext ? '.' + ext : ''}`;

            // Use the Backend Download API with Authentication token
            const token = localStorage.getItem('token');
            const downloadUrl = `${api.defaults.baseURL}/project-management/download?file=${encodeURIComponent(relativePath)}&name=${encodeURIComponent(finalName)}&token=${token}`;
            window.location.href = downloadUrl;
            
        } catch (err) {
            window.open(fileUrl, '_blank');
        }
    };

    const handleDeleteVersion = (version) => {
        setVersionToDelete(version);
    };

    const confirmDeleteVersion = async () => {
        if (!versionToDelete) return;
        
        try {
            const res = await api.delete(`/project-management/delete-version/${versionToDelete.id}`);
            if (res.data.status) {
                toast.success("Version entry deleted");
                setVersionToDelete(null);
                fetchDetails();
            }
        } catch (err) {
            toast.error("Failed to delete version entry");
        }
    };

    const fetchResources = async () => {
        try {
            const [statRes, empRes] = await Promise.all([
                api.get('/project-management/get-statuses'),
                api.get('/project-management/get-employees')
            ]);
            if (statRes.data.status) setStatuses(statRes.data.data.map(s => ({ value: s.id, label: s.status_name })));
            if (empRes.data.status) setEmpOptions(empRes.data.data.map(u => ({
                value: u.user_id,
                label: `${u.first_name} ${u.last_name} (${u.designation_name})`
            })));
        } catch (err) { console.error(err); }
    };

    const handleSubmitResponse = async () => {
        if (!newComment.trim() && !newStatusId && newAssigneeIds.length === 0) return;
        try {
            setSubmitting(true);
            const formData = new FormData();
            formData.append('status_id', newStatusId || task.status_id);
            formData.append('priority_id', newPriorityId || task.priority_id);
            formData.append('status_comment', newComment);
            
            // Keep basic info
            formData.append('project_name', task.project_name);
            formData.append('customer_name', task.customer_name);
            formData.append('title', task.title);
            formData.append('module_name', task.module_name);
            formData.append('vertical_head_id', task.vertical_head_id);
            formData.append('version_number', task.version_number);
            
            // Sync assignees (either new or existing)
            // If status is 6 or 9, it MUST go to Client (Handover)
            const isClientHandover = [6, 9].includes(Number(newStatusId || task.status_id));
            const finalAssignees = isClientHandover ? [] : (newAssigneeIds.length > 0 ? newAssigneeIds : (task.assignee_ids ? task.assignee_ids.split(',').map(Number) : []));
            formData.append('assigned_user_ids', finalAssignees.join(','));

            responseAttachments.forEach(file => formData.append('files[]', file));

            const res = await api.post(`/project-management/${taskId}`, formData);
            if (res.data.status) {
                toast.success('Response submitted successfully!');
                setNewComment('');
                setNewStatusId('');
                setNewPriorityId('');
                setNewAssigneeIds([]);
                setResponseAttachments([]);
                fetchDetails();
                onSuccess?.();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };


    const fetchDetails = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/project-management/${taskId}`);
            if (res.data?.status) {
                setTask(res.data.data);
            }
        } catch (err) {
            console.error("Failed to load task details", err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setResponseAttachments(prev => [...prev, ...newFiles]);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setResponseAttachments(prev => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        fetchDetails();
        fetchResources();
    }, [taskId]);

    const handleVersionSubmit = async () => {
        if (!versionForm.version_number) return toast.error("Version number is required");
        
        setSubmitting(true);
        const toastId = toast.loading("Saving version details...");
        try {
            const fd = new FormData();
            fd.append('version_number', versionForm.version_number);
            fd.append('notes', versionForm.notes);
            if (versionForm.previous_changes_doc) fd.append('previous_changes_doc', versionForm.previous_changes_doc);
            if (versionForm.updated_doc) fd.append('updated_doc', versionForm.updated_doc);

            const res = await api.post(`/project-management/${taskId}/add-version`, fd);
            if (res.data.status) {
                toast.update(toastId, { render: "Version created successfully!", type: "success", isLoading: false, autoClose: 3000 });
                setShowVersionForm(false);
                setShowVersionPopup(false);
                setVersionForm({ version_number: '', notes: '', previous_changes_doc: null, updated_doc: null });
                fetchDetails();
            }
        } catch (err) {
            toast.update(toastId, { render: "Error saving version", type: "error", isLoading: false, autoClose: 3000 });
        } finally {
            setSubmitting(false);
        }
    };


    const handleToggleClientVisibility = async () => {
        setSubmitting(true);
        try {
            const newState = task.is_visible_to_client == 1 ? 0 : 1;
            const res = await api.post(`/project-management/${taskId}`, { is_visible_to_client: newState });
            if (res.data.status) {
                toast.success(newState ? "Requirement shared with Client" : "Requirement hidden from Client");
                fetchDetails();
                onSuccess?.();
            }
        } catch (err) {
            toast.error("Failed to update client visibility");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="fixed inset-0 flex items-center justify-center z-50 bg-white/80"><Loader /></div>;
    if (!task) return null;

    const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL || (window.location.origin + '/ci4-api');

    return (
        <div 
            className="fixed top-0 left-0 w-screen h-screen z-[99999] flex justify-end bg-black/60 backdrop-blur-[3px]"
            style={{ margin: 0, padding: 0 }}
        >
            <div 
                className="relative h-full bg-white shadow-[-15px_0_30px_rgba(0,0,0,0.15)] flex flex-col animate-in slide-in-from-right duration-300"
                style={{ width: `${drawerWidth}px` }}
            >
                {/* Resize Handle */}
                <div 
                    onMouseDown={startResizing}
                    className="absolute left-0 top-0 w-3 h-full cursor-ew-resize hover:bg-main/5 transition-all z-20 group"
                    title="Drag to resize"
                >
                    {/* Visual Grip Symbol at Center */}
                    <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                        <div className="w-1.5 h-20 bg-main rounded-full shadow-[0_0_10px_rgba(255,0,0,0.3)] group-hover:h-24 transition-all duration-300"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1">
                            <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                            <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                            <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                        </div>
                    </div>
                </div>

                {/* Header */}
                <div className="bg-white px-8 pt-6 pb-4 border-b flex items-center justify-between sticky top-0 z-10 shadow-sm">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-x-3 mb-2 flex-wrap">
                            <span className="bg-main text-white text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shrink-0 flex items-center shadow-md shadow-main/20">
                                Task #{task.id}
                            </span>
                            <div className="flex items-center gap-1.5 text-gray-400 text-[10px] font-bold uppercase tracking-tight truncate">
                                <span>{task.customer_name}</span>
                                <span className="opacity-30">•</span>
                                <span>{task.project_name}</span>
                            </div>
                        </div>
                        <h2 className="text-2xl font-black text-gray-800 leading-tight tracking-tight">{task.title}</h2>
                    </div>
                    <div className="flex items-center gap-3 ml-4">

                        {/* Client Visibility: Only Admin / VH can toggle Client Visibility. Only if NOT Draft. */}
                        {(String(user?.role_id) === "1" || task.vertical_head_id == user?.user_id) && !showEdit && task.is_draft == 0 && (
                            <button 
                                onClick={handleToggleClientVisibility} 
                                disabled={submitting}
                                className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.1em] transition-all shadow-md flex items-center gap-2 border ${task.is_visible_to_client == 1 ? 'bg-emerald-500 text-white border-emerald-400 hover:bg-emerald-600 shadow-emerald-200' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 shadow-slate-100'}`}
                                title={task.is_visible_to_client == 1 ? "Currently visible to Client" : "Currently hidden from Client"}
                            >
                                <FiUser className="text-sm" />
                                {task.is_visible_to_client == 1 ? 'Hide from Client' : 'Visible to Client'}
                            </button>
                        )}
                        {String(user?.role_id) !== "2" && (
                            <button onClick={() => setShowEdit(!showEdit)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-gray-400 hover:bg-gray-50 transition-all shadow-md shadow-gray-100">
                                {showEdit ? 'Close Editor' : 'Edit Task'}
                            </button>
                        )}
                        <button onClick={onClose} className="p-3 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:rotate-90 transition-all duration-300"><FiX className="w-6 h-6" /></button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {showEdit ? (
                        <div className="p-4">
                            <ProjectManagementForm 
                                taskId={taskId} 
                                initialData={task} 
                                onClose={() => setShowEdit(false)} 
                                onSuccess={() => {
                                    fetchDetails();
                                    setShowEdit(false);
                                    onSuccess?.();
                                }} 
                            />
                        </div>
                    ) : (
                        <div className="px-6 pt-2 pb-6 space-y-6">
                             {/* Stats Grid */}
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                <DetailCard icon={<FiActivity className="text-blue-500" />} label="Status" value={task.status_name} />
                                <DetailCard icon={<FiClock className="text-orange-500" />} label="Priority" value={task.priority_id == 1 ? 'Low' : task.priority_id == 2 ? 'Medium' : task.priority_id == 3 ? 'High' : 'Critical'} />
                                <DetailCard icon={<FiUser className="text-purple-500" />} label="Assigned To" value={[6, 9].includes(Number(task.status_id)) ? task.customer_name : (task.assigned_users || 'Unassigned')} />
                                <DetailCard icon={<FiCheckSquare className="text-emerald-500" />} label="System Modules" value={task.system_modules} />
                                <DetailCard icon={<FiUser className="text-indigo-500" />} label="Vertical Head" value={`${task.vh_first_name} ${task.vh_last_name}`} />
                            </div>

                            {/* Details Content */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-6 space-y-6">
                                    <Section 
                                        title="Requirement Description" 
                                        extra={task.document_file && (
                                            <button 
                                                onClick={() => handleDownload(`${IMAGE_BASE_URL}/${task.document_file}`, 'original')}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-main text-white text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-main-dark transition-all shadow-sm"
                                            >
                                                <FiDownload className="text-xs" />
                                                Original Doc
                                            </button>
                                        )}
                                    >
                                        <div className={`relative transition-all duration-500 overflow-hidden ${!isExpanded ? 'max-h-[200px]' : 'max-h-full'}`}>
                                            <div className="text-sm text-gray-600 leading-relaxed rich-text-content" dangerouslySetInnerHTML={{ __html: task.description }} />
                                            {!isExpanded && (
                                                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                            )}
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-start">
                                            <button 
                                                onClick={() => setIsExpanded(!isExpanded)}
                                                className="text-[10px] font-black uppercase tracking-[0.2em] text-main hover:text-red-700 transition-colors flex items-center gap-1.5"
                                            >
                                                {isExpanded ? 'Show Less' : 'Read Full Requirement'}
                                                <div className={`w-1.5 h-1.5 rounded-full bg-main ${isExpanded ? '' : 'animate-ping'}`} />
                                            </button>
                                        </div>
                                    </Section>

                                    <div className="grid grid-cols-1 gap-6">
                                        {String(user?.role_id) !== "2" && (
                                            <Section title="Internal Notes">
                                                <div className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: task.internal_notes || 'No notes available.' }} />
                                            </Section>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Responsive Dashboard Row */}
                            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 pb-6 border-b border-gray-100">
                                {/* Submit Response Area - 70% */}
                                <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-100 p-4 space-y-4 shadow-sm flex flex-col">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                        <FiUser /> Submit Response
                                    </h3>
                                    <div className={`grid grid-cols-1 ${String(user?.role_id) === "2" || [6, 9].includes(Number(newStatusId || task.status_id)) ? '' : 'md:grid-cols-2'} gap-3`}>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Status</label>
                                            <Select 
                                                options={statuses}
                                                value={statuses.find(s => s.value === (newStatusId || task.status_id))}
                                                onChange={opt => {
                                                    setNewStatusId(opt.value);
                                                    if ([6, 9].includes(Number(opt.value))) setNewAssigneeIds([]);
                                                }}
                                                className="text-[11px] font-bold"
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                                            />
                                        </div>
                                        {String(user?.role_id) !== "2" && ![6, 9].includes(Number(newStatusId || task.status_id)) && (
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Assigned To</label>
                                                <Select 
                                                    isMulti
                                                    options={empOptions}
                                                    value={empOptions.filter(o => newAssigneeIds.length > 0 ? newAssigneeIds.includes(o.value) : (task.assignee_ids?.split(',').map(Number).includes(o.value)))}
                                                    onChange={opts => setNewAssigneeIds(opts ? opts.map(o => o.value) : [])}
                                                    className="text-[11px] font-bold"
                                                    placeholder="Forward to..."
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-1 flex-1">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Remark / Response</label>
                                        <ReactQuill 
                                            theme="snow"
                                            value={newComment}
                                            onChange={setNewComment}
                                            placeholder="Type your response here..."
                                            className="custom-quill bg-white rounded-xl border border-gray-100 text-xs shadow-sm"
                                        />
                                    </div>

                                    {/* Selected Files List */}
                                    {responseAttachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {responseAttachments.map((file, idx) => (
                                                <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-2 py-1 rounded-md group">
                                                    <HiPaperClip className="text-[10px] text-gray-400" />
                                                    <span className="text-[9px] font-bold text-gray-500 truncate max-w-[120px]">{file.name}</span>
                                                    <button 
                                                        onClick={() => removeFile(idx)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <FiX className="text-[10px]" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between gap-4 pt-2">
                                        <div className="relative group">
                                            <input type="file" multiple onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                                            <div className="border border-dashed border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2 group-hover:bg-gray-50 transition-all group-hover:border-main/20">
                                                <HiOutlineCloudUpload className="text-gray-400 group-hover:text-main" />
                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Attach Files ({responseAttachments.length})</span>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={handleSubmitResponse}
                                            disabled={submitting || (!newComment.trim() && !newStatusId && newAssigneeIds.length === 0)}
                                            className={`flex items-center gap-2 px-8 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-md ${submitting || (!newComment.trim() && !newStatusId && newAssigneeIds.length === 0) ? 'bg-gray-100 text-gray-400' : 'bg-red-600 text-white hover:bg-red-700 active:scale-95 shadow-red-100'}`}
                                        >
                                            {submitting ? 'Sending...' : 'Submit Response'}
                                        </button>
                                    </div>
                                </div>

                                {/* Project Attributes, Versions & Resources - 30% */}
                                <div className="lg:col-span-3 space-y-4 flex flex-col h-full">
                                    {/* Project Details Card */}
                                    <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                            <FiActivity /> Project Details
                                        </h3>
                                        <div className="space-y-3">
                                            <InfoRows label="Module :" value={task.module_name} />
                                            <InfoRows label="SOP Ref :" value={task.sop_ref || '-'} />
                                            <InfoRows label="Version :" value={task.version_number} />
                                            <div className="h-px bg-gray-50 my-1" />
                                            <InfoRows label="Start Date :" value={task.expected_start_date && !isNaN(new Date(task.expected_start_date).getTime()) ? format(new Date(task.expected_start_date), 'dd/MM/yyyy') : '-'} />
                                            <InfoRows label="Expect. Comp :" value={task.expected_completion_date && !isNaN(new Date(task.expected_completion_date).getTime()) ? format(new Date(task.expected_completion_date), 'dd/MM/yyyy') : '-'} />
                                        </div>
                                    </div>

                                    {/* Version History Container */}
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex-1 flex flex-col">
                                        <div className="bg-slate-50 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                                <HiDocumentText className="text-lg text-blue-500" /> Version History
                                            </h3>
                                        </div>

                                        <div className="p-4">
                                            <button 
                                                onClick={() => setShowVersionPopup(true)}
                                                className="w-full flex items-center justify-between gap-3 p-3 bg-white hover:bg-main hover:text-white rounded-xl border border-gray-100 hover:border-main shadow-sm transition-all group group-hover:shadow-md"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-blue-50 text-blue-500 rounded-lg group-hover:bg-white/20 group-hover:text-white">
                                                        <FiActivity className="text-lg" />
                                                    </div>
                                                    <div className="text-left">
                                                        <p className="text-[10px] font-black uppercase tracking-widest leading-none">View Full History</p>
                                                        <p className="text-[9px] font-bold opacity-60 group-hover:opacity-80 mt-1">
                                                            {task.activity_log?.filter(l => l.version_number).length || 0} Versions logged
                                                        </p>
                                                    </div>
                                                </div>
                                                <HiOutlineCloudUpload className="text-lg opacity-40 group-hover:opacity-100" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Version History Popup */}
                            {showVersionPopup && (
                                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                                    <div className="bg-slate-50 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                                        <div className="bg-white px-6 py-4 border-b flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 bg-red-100 text-red-600 rounded-2xl">
                                                    <HiDocumentText className="text-2xl" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-black text-gray-800 tracking-tight">Requirement Versioning</h3>
                                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Historical comparison & updates</p>
                                                </div>
                                            </div>
                                            <button onClick={() => setShowVersionPopup(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"><FiX className="text-xl" /></button>
                                        </div>

                                        <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-[400px]">
                                            {/* Comparison Timeline - 60% */}
                                            <div className={`${String(user?.role_id) === "2" ? 'w-full' : 'flex-1'} p-6 overflow-y-auto custom-scrollbar bg-white`}>
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                    <FiActivity className="text-red-500" /> {String(user?.role_id) === "2" ? 'Active Specification' : 'Previous Versions'}
                                                </h4>
                                                
                                                <div className="space-y-4">
                                                    {(!task.activity_log || task.activity_log.filter(l => l.version_number).length === 0) ? (
                                                        <div className="text-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                                                            <HiDocumentText className="text-4xl text-gray-200 mx-auto mb-3" />
                                                            <p className="text-sm text-gray-400 font-bold uppercase tracking-widest italic">No version history found</p>
                                                        </div>
                                                    ) : task.activity_log
                                                        .filter(l => l.version_number)
                                                        .filter((v, i) => String(user?.role_id) !== "2" || i === 0)
                                                        .map((v, i) => (
                                                        <div key={v.id} className={`p-6 rounded-2xl border transition-all ${i === 0 ? 'bg-gradient-to-br from-white to-red-50/30 border-red-200 ring-2 ring-red-50' : 'bg-gray-50/50 border-gray-100'}`}>
                                                            <div className="flex justify-between items-start mb-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={`p-2 rounded-xl ${i === 0 ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                                        <span className="text-xs font-black italic">V</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-base font-black text-gray-800 tracking-tight">Version {v.version_number}</span>
                                                                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">{format(new Date(v.created_at), 'dd MMM yyyy • HH:mm')}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {i === 0 && <span className="text-[9px] font-black text-red-600 bg-red-100 px-3 py-1 rounded-full uppercase tracking-widest border border-red-200">Active Specification</span>}
                                                                    {String(user?.role_id) === "1" && (
                                                                        <button 
                                                                            onClick={() => handleDeleteVersion(v)}
                                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                            title="Delete Version Entry"
                                                                        >
                                                                            <FiTrash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                {v.previous_changes_doc && (
                                                                    <a 
                                                                        href={`${IMAGE_BASE_URL}/${v.previous_changes_doc}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="flex flex-col gap-2 p-4 bg-white rounded-2xl border border-orange-100 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-500/10 transition-all group"
                                                                    >
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-[9px] font-black text-orange-600 bg-orange-50 px-2 py-1 rounded uppercase tracking-widest">Modification Log</span>
                                                                            <FiDownload className="text-orange-400 group-hover:scale-125 transition-transform" />
                                                                        </div>
                                                                        <span className="text-xs font-bold text-gray-700 mt-1">View Shift Summary</span>
                                                                    </a>
                                                                )}
                                                                {v.updated_doc && (
                                                                    <a 
                                                                        href={`${IMAGE_BASE_URL}/${v.updated_doc}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="flex flex-col gap-2 p-4 bg-white rounded-2xl border border-emerald-100 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/10 transition-all group"
                                                                    >
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded uppercase tracking-widest">Updated Spec</span>
                                                                            <FiDownload className="text-emerald-400 group-hover:scale-125 transition-transform" />
                                                                        </div>
                                                                        <span className="text-xs font-bold text-gray-700 mt-1">View Final Document</span>
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Version Form Pane - Hide for clients */}
                                            {String(user?.role_id) !== "2" && (
                                                <div className="w-full md:w-[350px] p-8 bg-slate-100 border-l border-gray-200">
                                                    <div className="sticky top-0">
                                                        <div className="flex items-center gap-3 mb-8">
                                                            <div className="p-3 bg-main text-white rounded-2xl shadow-lg shadow-main/30">
                                                                <HiOutlineCloudUpload className="text-2xl" />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-black text-gray-800 tracking-tight">Push Update</h4>
                                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Apply Next Version</p>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-5">
                                                            <div className="space-y-2">
                                                                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">New Version Identifier *</label>
                                                                <input 
                                                                    className={`w-full bg-white border ${!versionForm.version_number && submitting ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'border-gray-200'} rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-main/10 focus:border-main transition-all`}
                                                                    placeholder="e.g. V1.0.4"
                                                                    value={versionForm.version_number}
                                                                    onChange={e => setVersionForm(prev => ({ ...prev, version_number: e.target.value }))}
                                                                />
                                                            </div>
                                                            
                                                            <div className="space-y-4">
                                                                <div className="space-y-1">
                                                                    <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-3xl cursor-pointer hover:bg-white hover:border-orange-400 transition-all group ${versionForm.previous_changes_doc ? 'bg-orange-50 border-orange-400' : ''}`}>
                                                                        <div className={`p-3 rounded-2xl mb-2 ${versionForm.previous_changes_doc ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                                            <FiDownload className="text-xl" />
                                                                        </div>
                                                                        <span className="text-[9px] font-black text-gray-500 uppercase group-hover:text-orange-600">Previous Changes</span>
                                                                        {versionForm.previous_changes_doc && <span className="text-[8px] text-orange-800 mt-2 font-bold truncate w-full text-center px-4">{versionForm.previous_changes_doc.name}</span>}
                                                                        <input type="file" className="hidden" onChange={e => setVersionForm(prev => ({ ...prev, previous_changes_doc: e.target.files[0] }))} />
                                                                    </label>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-3xl cursor-pointer hover:bg-white hover:border-emerald-400 transition-all group ${versionForm.updated_doc ? 'bg-emerald-50 border-emerald-400' : ''}`}>
                                                                        <div className={`p-3 rounded-2xl mb-2 ${versionForm.updated_doc ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                                            <HiOutlineCloudUpload className="text-xl" />
                                                                        </div>
                                                                        <span className="text-[9px] font-black text-gray-500 uppercase group-hover:text-emerald-600">Updated Final Doc</span>
                                                                        {versionForm.updated_doc && <span className="text-[8px] text-emerald-800 mt-2 font-bold truncate w-full text-center px-4">{versionForm.updated_doc.name}</span>}
                                                                        <input type="file" className="hidden" onChange={e => setVersionForm(prev => ({ ...prev, updated_doc: e.target.files[0] }))} />
                                                                    </label>
                                                                </div>
                                                            </div>

                                                            <div className="pt-4">
                                                                <button 
                                                                    disabled={submitting}
                                                                    onClick={handleVersionSubmit}
                                                                    className="w-full bg-main text-white py-4 rounded-3xl text-xs font-black uppercase tracking-widest shadow-2xl shadow-main/40 hover:bg-main-dark disabled:opacity-50 transition-all active:scale-95"
                                                                >
                                                                    {submitting ? 'Processing Version...' : 'Apply Version Update'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Full Width Activity History */}
                            <div className="space-y-6 pt-6">
                                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                    <FiActivity className="text-main" /> Full Activity History
                                </h3>
                                <div className="space-y-6">
                                    {task.activity_log?.map((log, idx) => (
                                        <div key={idx} className="flex gap-4 relative w-full">
                                            {/* Timeline Line */}
                                            <div className="absolute left-[15px] top-[40px] bottom-[-24px] w-[2px] bg-gray-100" />
                                            
                                            <div className="w-8 h-8 rounded-full bg-main/10 flex items-center justify-center shrink-0 z-10 overflow-hidden">
                                                <FiUser className="text-main" />
                                            </div>

                                            <div className="flex-1 space-y-3 pb-8">
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-black text-gray-800">{log.first_name} {log.last_name}</span>
                                                        <span className="px-2 py-0.5 bg-gray-100 rounded text-[10px] font-bold text-gray-400 uppercase tracking-tight">Team</span>
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{log.created_at ? format(new Date(log.created_at), 'dd MMM yyyy HH:mm') : '-'}</span>
                                                </div>
                                                
                                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 relative group hover:border-main/20 transition-all w-full">
                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        <span className="px-2 py-1 rounded border border-blue-500 text-blue-600 bg-white text-[10px] font-bold uppercase tracking-tight">
                                                            Status: {log.to_status}
                                                        </span>
                                                        {log.priority_name && (
                                                            <span className={`px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-tight bg-white ${
                                                                log.priority_name === 'Critical' ? 'border-red-700 text-red-700' :
                                                                log.priority_name === 'High' ? 'border-orange-500 text-orange-500' :
                                                                log.priority_name === 'Medium' ? 'border-yellow-500 text-yellow-600' :
                                                                'border-gray-500 text-gray-500'
                                                            }`}>
                                                                Priority: {log.priority_name}
                                                            </span>
                                                        )}
                                                        <span className="px-2 py-1 rounded border border-emerald-600 text-emerald-600 bg-white text-[10px] font-bold uppercase tracking-tight">
                                                            Assigned To: {[6, 9].includes(Number(log.to_status_id)) ? task.customer_name : (log.assignee_names || '-')}
                                                        </span>
                                                    </div>

                                                    {log.comment && (
                                                        <div className="text-sm text-gray-700 leading-relaxed rich-text-content">
                                                            <div dangerouslySetInnerHTML={{ __html: log.comment }} />
                                                        </div>
                                                    )}

                                                    {log.attachments?.length > 0 && (
                                                        <div className="mt-4 pt-4 border-t border-gray-50">
                                                            <div className="flex flex-wrap gap-2">
                                                                {log.attachments.map((file, fIdx) => (
                                                                    <button 
                                                                        key={fIdx} 
                                                                        onClick={() => handleDownload(`${IMAGE_BASE_URL}/${file.file_path}`, log.to_status || 'update', file.file_name)}
                                                                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-[9px] font-bold text-gray-500 border border-gray-100 hover:text-main hover:bg-main/5 hover:border-main/20 transition-all"
                                                                    >
                                                                        <HiPaperClip /> <span className="truncate max-w-[150px]">{file.file_name}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Version Delete Confirmation Modal */}
            {versionToDelete && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xs shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-red-50">
                        <div className="p-8 text-center space-y-4">
                            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-2 animate-bounce">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-gray-800 tracking-tight">Are you sure?</h3>
                            <p className="text-sm text-gray-500 font-medium leading-relaxed">
                                You are about to delete <span className="text-red-600 font-bold">Version {versionToDelete.version_number}</span>. This action cannot be undone.
                            </p>
                            <div className="flex gap-3 pt-4">
                                <button 
                                    onClick={() => setVersionToDelete(null)}
                                    className="flex-1 py-3 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={confirmDeleteVersion}
                                    className="flex-1 py-3 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-lg shadow-red-200 uppercase tracking-widest"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailCard({ icon, label, value }) {
    return (
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg shrink-0 self-start mt-1">{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{label}</p>
                <p className="text-sm font-black text-gray-800 break-words leading-tight">{value || '-'}</p>
            </div>
        </div>
    );
}

function Section({ title, children, extra }) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">{title}</h4>
                {extra}
            </div>
            {children}
        </div>
    );
}

function InfoRows({ label, value, highlight }) {
    return (
        <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">{label}</span>
            <span className={`font-bold ${highlight ? 'text-main' : 'text-gray-800'}`}>{value}</span>
        </div>
    );
}
